import { config } from '@/config';
/**
 * 获取完整的图片URL
 * @param relativePath 相对路径，如 "/images/2025/08/30/abc.jpg"
 * @returns 完整的图片URL
 */
export function getImageUrl(relativePath) {
    if (!relativePath) {
        return '';
    }
    // 如果已经是完整URL，直接返回
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
        return relativePath;
    }
    // 拼接静态资源基础URL
    return config.staticBaseUrl + relativePath;
}
/**
 * 获取图片预览URL列表
 * @param relativePath 相对路径
 * @returns 预览URL数组
 */
export function getImagePreviewList(relativePath) {
    const url = getImageUrl(relativePath);
    return url ? [url] : [];
}
function getImageType(file) {
    return (file.type || '').toLowerCase();
}
function isPng(file) {
    return getImageType(file) === 'image/png';
}
function isJpegLike(file) {
    const t = getImageType(file);
    return t === 'image/jpeg' || t === 'image/jpg';
}
async function readAsImageBitmap(file) {
    // 优先使用 createImageBitmap（更快且无布局依赖）
    try {
        // 在 Safari 部分版本可能不支持，走 fallback
        if (typeof createImageBitmap === 'function') {
            return await createImageBitmap(file);
        }
    }
    catch {
        // ignore
    }
    return await readAsHTMLImageElement(file).then(img => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        return createImageBitmap(canvas);
    });
}
function readAsHTMLImageElement(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = (e) => reject(e);
            img.src = String(reader.result);
        };
        reader.onerror = (e) => reject(e);
        reader.readAsDataURL(file);
    });
}
function calcTargetSize(width, height, opts) {
    let targetW = width;
    let targetH = height;
    // 先按上限约束等比缩放
    const scaleW = opts.maxWidth > 0 ? opts.maxWidth / targetW : 1;
    const scaleH = opts.maxHeight > 0 ? opts.maxHeight / targetH : 1;
    const scale = Math.min(1, scaleW, scaleH);
    if (scale < 1) {
        targetW = Math.max(Math.round(targetW * scale), opts.minWidth);
        targetH = Math.max(Math.round(targetH * scale), opts.minHeight);
    }
    return { width: targetW, height: targetH };
}
function toBlob(canvas, type, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob)
                resolve(blob);
            else
                reject(new Error('canvas.toBlob failed'));
        }, type, quality);
    });
}
async function drawScaledCanvas(bitmap, targetW, targetH) {
    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    return canvas;
}
export async function compressImageFile(file, options) {
    const opts = {
        maxSizeMB: config.upload.maxSizeMB,
        jpegQuality: config.upload.jpegQuality,
        minJpegQuality: config.upload.minJpegQuality,
        maxWidth: config.upload.maxWidth,
        maxHeight: config.upload.maxHeight,
        minWidth: config.upload.minWidth,
        minHeight: config.upload.minHeight,
        pngScaleOnly: config.upload.pngScaleOnly,
        ...options
    };
    // 已满足容量要求则直接返回
    const maxBytes = opts.maxSizeMB * 1024 * 1024;
    if (file.size <= maxBytes) {
        return file;
    }
    // 仅处理图片类型
    if (!file.type || !file.type.startsWith('image/')) {
        return file;
    }
    // 解码并缩放
    const bitmap = await readAsImageBitmap(file);
    const { width, height } = bitmap;
    const size = calcTargetSize(width, height, opts);
    const canvas = await drawScaledCanvas(bitmap, size.width, size.height);
    // 输出类型策略
    const outputType = opts.outputMime || (isPng(file) && opts.pngScaleOnly ? 'image/png' : (isJpegLike(file) ? 'image/jpeg' : 'image/jpeg'));
    // PNG：不转码，仅按尺寸缩放
    if (outputType === 'image/png') {
        const blob = await toBlob(canvas, 'image/png');
        // 若依然超过限制，返回该 PNG（通常很少发生；如发生需用户另行处理）
        if (blob.size <= maxBytes) {
            return new File([blob], file.name.replace(/\.[^.]+$/, '.png'), { type: 'image/png' });
        }
        return new File([blob], file.name.replace(/\.[^.]+$/, '.png'), { type: 'image/png' });
    }
    // JPEG：迭代降低质量直至满足要求或触及下限
    const qHigh = Math.min(1, Math.max(0.1, opts.jpegQuality));
    const qLow = Math.min(qHigh, Math.max(0.1, opts.minJpegQuality));
    let bestBlob = null;
    // 先尝试初始质量
    const blob = await toBlob(canvas, 'image/jpeg', qHigh);
    if (blob.size <= maxBytes) {
        bestBlob = blob;
    }
    else {
        // 二分逼近
        let left = qLow;
        let right = qHigh;
        for (let i = 0; i < 6; i++) { // 6次足够逼近
            const mid = (left + right) / 2;
            const tmp = await toBlob(canvas, 'image/jpeg', mid);
            if (tmp.size <= maxBytes) {
                bestBlob = tmp;
                left = mid;
            }
            else {
                right = mid;
            }
            if (Math.abs(right - left) < 0.02)
                break;
        }
        // 若依旧未找到合格结果，使用最低质量再试一次
        if (!bestBlob) {
            const minBlob = await toBlob(canvas, 'image/jpeg', qLow);
            bestBlob = minBlob;
        }
    }
    const outBlob = bestBlob || blob;
    return new File([outBlob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
}
/**
 * 尝试将文件压缩到不超过最大体积，若已符合则原样返回。
 */
export async function ensureMaxSize(file, options) {
    try {
        return await compressImageFile(file, options);
    }
    catch {
        // 压缩失败时不阻断流程，回退原文件
        return file;
    }
}
export default {
    getImageUrl,
    getImagePreviewList,
    compressImageFile,
    ensureMaxSize
};
